import React, { Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Main from 'components/Main';
import LoadingZone from 'components/LoadingZone';
import RequireAuth from './RequireAuth';

import {
  LandingPage,
  SignUp,
  UserMatching,
  MatchValidation,
  MagicLink,
  TermsOfUseAcceptancePage,
  SignUpHelp,
  ProfileExternalView,
  Philanthropy,
  GivingCheckout,
  GivingReview,
  GivingConfirmation,
  GivingTrafficController,
  PledgePaymentCheckout,
  PledgePaymentReview,
  PledgePaymentConfirmation,
  EventDetailsPage,
  EventRegistrationContainer,
  GuestManagement,
  RegistrationOptionsForm,
  EventRegistrationCheckpoint,
  ReviewRegistration,
  AdditionalInfo,
  EventRegistrationContactInfo,
  EventConfirmation,
  EventRegistrationUserIdentification,
  NotInvited,
  EventWaitlist,
  Volunteer,
  Scholarships,
  Settings,
  Downtime,
  NotFound,
  ContactUs,
  StyleGuide,
  ComponentLibrary,
} from './LazyRouteComponents';

const AppRoutes = ({ appProps }) => {
  const bIsAuthenticated = !!appProps.user;
  return (
    <Suspense fallback={<LoadingZone isLoading />}>
      <Routes>
        <Route path='/' element={<Navigate to='/main' />} />
        <Route path='/signin' element={<Navigate to='/sign-in' />} />
        <Route
          path='sign-in/:type?'
          element={
            <LandingPage aMaintenanceAlerts={appProps.aMaintenanceAlerts} />
          }
        />
        <Route
          path='giving-traffic-controller'
          element={<GivingTrafficController oUser={appProps.user} />}
        />
        <Route path='sign-up' element={<SignUp />} />
        <Route path='sign-up/match/:type' element={<UserMatching />} />
        <Route path='sign-up/magic-link/:type' element={<MagicLink />} />
        <Route path='sign-up/validation/:type' element={<MatchValidation />} />
        <Route
          path='sign-up/finalize-account'
          element={<TermsOfUseAcceptancePage />}
        />
        <Route path='sign-up/help' element={<SignUpHelp />} />
        <Route path='sign-up/help/:type' element={<SignUpHelp />} />
        <Route path='downtime' element={<Downtime />} />
        <Route
          path='main'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <Main />
            </RequireAuth>
          }
        >
          <Route
            path='profile/:lookupId'
            element={
              <RequireAuth bIsAuthenticated={bIsAuthenticated}>
                <ProfileExternalView />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path='main/search'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <Main />
            </RequireAuth>
          }
        />
        <Route
          path='events/:eventSlug'
          element={<EventDetailsPage bIsAuthenticated={bIsAuthenticated} />}
        >
          <Route path='profile/:lookupId' element={<ProfileExternalView />} />
        </Route>
        <Route
          path='events/:eventSlug/register'
          element={<EventRegistrationContainer />}
        >
          <Route path='waitlist' element={<EventWaitlist />} />
          <Route path='guests' element={<GuestManagement />} />
          <Route path='reg-options' element={<RegistrationOptionsForm />} />
          <Route path='checkpoint' element={<EventRegistrationCheckpoint />} />
        </Route>
        <Route path='events/register' element={<EventRegistrationContainer />}>
          <Route
            path='user-identification'
            element={<EventRegistrationUserIdentification />}
          />
          <Route path='checkpoint' element={<EventRegistrationCheckpoint />} />
          <Route path='additional-info' element={<AdditionalInfo />} />
          <Route path='contact' element={<EventRegistrationContactInfo />} />
          <Route path='review' element={<ReviewRegistration />} />
          <Route path='confirmation' element={<EventConfirmation />} />
        </Route>
        <Route path='events/register/invite-only' element={<NotInvited />} />
        <Route
          path='philanthropy'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <Philanthropy />
            </RequireAuth>
          }
        >
          <Route
            path='profile/:lookupId'
            element={
              <RequireAuth bIsAuthenticated={bIsAuthenticated}>
                <ProfileExternalView />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path='philanthropy/giving'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <GivingCheckout />
            </RequireAuth>
          }
        />
        <Route
          path='philanthropy/giving/review'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <GivingReview />
            </RequireAuth>
          }
        />
        <Route
          path='philanthropy/giving/confirmation'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <GivingConfirmation />
            </RequireAuth>
          }
        />
        <Route
          path='philanthropy/commitment-payment'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <PledgePaymentCheckout />
            </RequireAuth>
          }
        />
        <Route
          path='philanthropy/commitment-payment/review'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <PledgePaymentReview />
            </RequireAuth>
          }
        />
        <Route
          path='philanthropy/commitment-payment/confirmation'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <PledgePaymentConfirmation />
            </RequireAuth>
          }
        />
        <Route
          path='scholarships'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <Scholarships />
            </RequireAuth>
          }
        >
          <Route
            path='profile/:lookupId'
            element={
              <RequireAuth bIsAuthenticated={bIsAuthenticated}>
                <ProfileExternalView />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path='volunteers'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <Volunteer />
            </RequireAuth>
          }
        >
          <Route
            path='profile/:lookupId'
            element={
              <RequireAuth bIsAuthenticated={bIsAuthenticated}>
                <ProfileExternalView />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path='settings'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path='contact-us'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <ContactUs />
            </RequireAuth>
          }
        />
        <Route
          path='style-guide'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <StyleGuide />
            </RequireAuth>
          }
        />
        <Route
          path='component-library'
          element={
            <RequireAuth bIsAuthenticated={bIsAuthenticated}>
              <ComponentLibrary />
            </RequireAuth>
          }
        />
        <Route path='*' element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

AppRoutes.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  appProps: PropTypes.object.isRequired,
};

export default AppRoutes;
